import React from 'react';
// @ts-ignore - Remotion型定義の互換性問題の暫定対応
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

export interface TikTokStyleProps {
  headline: string;
  bodyLines: string[];
  ctaText: string;
  primaryColor: string;
  accentColor: string;
  aspectRatio: '1:1' | '16:9' | '9:16';
}

export const TikTokStyle: React.FC<TikTokStyleProps> = ({
  headline,
  bodyLines,
  ctaText,
  primaryColor,
  accentColor,
  aspectRatio,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const isPortrait = aspectRatio === '9:16';

  // Background pulse animation
  const bgPulse = interpolate(
    Math.sin(frame / 15),
    [-1, 1],
    [0.95, 1.05]
  );

  // Headline slam-in animation
  const headlineScale = spring({
    frame: frame - 5,
    fps,
    config: { damping: 8, mass: 0.5 },
  });

  const headlineOpacity = interpolate(frame, [0, 10], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // Body lines stagger animation
  const getLineAnimation = (index: number) => {
    const startFrame = 40 + index * 25;
    const opacity = interpolate(frame, [startFrame, startFrame + 15], [0, 1], {
      extrapolateRight: 'clamp',
    });
    const translateX = interpolate(frame, [startFrame, startFrame + 15], [80, 0], {
      extrapolateRight: 'clamp',
    });
    return { opacity, translateX };
  };

  // CTA bounce animation
  const ctaStartFrame = 40 + bodyLines.length * 25 + 20;
  const ctaScale = spring({
    frame: frame - ctaStartFrame,
    fps,
    config: { damping: 6, mass: 0.4 },
  });
  const ctaOpacity = interpolate(frame, [ctaStartFrame, ctaStartFrame + 10], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const ctaBounce = frame > ctaStartFrame + 30
    ? Math.sin((frame - ctaStartFrame - 30) / 8) * 3
    : 0;

  // Decorative floating particles
  const particles = [0, 1, 2, 3, 4].map((i) => ({
    x: interpolate(frame + i * 50, [0, durationInFrames], [10 + i * 18, 90 - i * 12]),
    y: interpolate(Math.sin((frame + i * 30) / 20), [-1, 1], [10, 90]),
    opacity: 0.15 + Math.sin((frame + i * 40) / 25) * 0.1,
    size: 4 + i * 2,
  }));

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${accentColor} 100%)`,
        fontFamily: "'Hiragino Sans', 'Noto Sans JP', sans-serif",
        overflow: 'hidden',
      }}
    >
      {/* Animated background circles */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: `${p.size}%`,
            height: `${p.size}%`,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            opacity: p.opacity,
            transform: `scale(${bgPulse})`,
          }}
        />
      ))}

      {/* Main content container */}
      <AbsoluteFill
        style={{
          justifyContent: 'center',
          alignItems: 'center',
          padding: isPortrait ? '60px 30px' : '40px 80px',
        }}
      >
        {/* Headline */}
        <div
          style={{
            opacity: headlineOpacity,
            transform: `scale(${headlineScale})`,
            marginBottom: isPortrait ? '40px' : '30px',
          }}
        >
          <h1
            style={{
              fontSize: isPortrait ? '52px' : '64px',
              fontWeight: 900,
              color: '#ffffff',
              textAlign: 'center',
              lineHeight: 1.2,
              textShadow: '0 4px 20px rgba(0,0,0,0.3)',
              letterSpacing: '-1px',
            }}
          >
            {headline}
          </h1>
        </div>

        {/* Body lines */}
        <div style={{ width: '100%', maxWidth: isPortrait ? '100%' : '800px' }}>
          {bodyLines.slice(0, 4).map((line, index) => {
            const anim = getLineAnimation(index);
            return (
              <div
                key={index}
                style={{
                  opacity: anim.opacity,
                  transform: `translateX(${anim.translateX}px)`,
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    width: isPortrait ? '8px' : '10px',
                    height: isPortrait ? '8px' : '10px',
                    borderRadius: '50%',
                    backgroundColor: accentColor,
                    flexShrink: 0,
                    boxShadow: `0 0 10px ${accentColor}`,
                  }}
                />
                <span
                  style={{
                    fontSize: isPortrait ? '22px' : '28px',
                    color: 'rgba(255,255,255,0.95)',
                    fontWeight: 600,
                  }}
                >
                  {line}
                </span>
              </div>
            );
          })}
        </div>

        {/* CTA Button */}
        <div
          style={{
            opacity: ctaOpacity,
            transform: `scale(${ctaScale}) translateY(${ctaBounce}px)`,
            marginTop: isPortrait ? '40px' : '30px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              color: primaryColor,
              padding: isPortrait ? '16px 48px' : '18px 56px',
              borderRadius: '50px',
              fontSize: isPortrait ? '24px' : '28px',
              fontWeight: 800,
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
            }}
          >
            {ctaText}
          </div>
        </div>
      </AbsoluteFill>

      {/* Bottom progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: '4px',
          width: `${interpolate(frame, [0, durationInFrames], [0, 100])}%`,
          background: '#ffffff',
          opacity: 0.6,
        }}
      />
    </AbsoluteFill>
  );
};
